/**
 * Shared word-status mutation for the feed transcript + word panel.
 *
 * Single source of truth for "toggle a word known/learning" so the word-panel
 * checkmark (VideoLayout.handleToggleWordKnown) and the transcript double-tap
 * gesture (Transcript.handleWordTap) behave identically — mirroring iOS, where
 * both the speech-bubble checkmark and `handleDoubleTap` call the same status
 * update (VideoScreenViewController.swift handleDoubleTap / updateWordStatus).
 */
import type { UserVideoWord, UserWordStatus } from "@shared_types";
import { callWithAuth, putWordStatus } from "@/api/Api";
import { REVIEW_CARD_COUNT_QUERY_KEY } from "@/components/Review/ReviewInsights";
import { priorityWordTracker } from "@/activities";
import { globalQueryClient } from "@/lib/queryClient";
import {
  wordKnownToast$,
  peekWordOverride,
  setWordOverride,
} from "../state/videoState";

// Defer a first-tap "learning" persist by just over the transcript's 300ms
// double-tap window, so a double-tap (→ "known") cancels it before it sends.
const LEARNING_WRITE_DEFER_MS = 350;

// Pending (deferred) first-tap learning writes, keyed by the canonical wordKey
// (matching wordStatusOverrides$). Module scoped — not component state — so the
// timer fires regardless of which screen is mounted (no dropped save on
// unmount/feed-swipe) and so ANY surface that promotes the word to "known"
// (transcript double-tap OR the panel checkmark) can cancel it via
// `cancelPendingLearningWrite`. We keep the pre-tap status so a caller that
// cancels it can roll back to the TRUE persisted status (the learning write
// never reached the server) rather than the optimistic value.
interface PendingLearningWrite {
  timer: ReturnType<typeof setTimeout>;
  /** The persisted status before the optimistic "learning" (e.g. "new"). */
  prevStatus: UserWordStatus;
}
const pendingLearningWrites = new Map<string, PendingLearningWrite>();

/** Cancel a pending learning write; returns its pre-tap status if one existed. */
export function cancelPendingLearningWrite(
  wordKey: string,
): UserWordStatus | undefined {
  const pending = pendingLearningWrites.get(wordKey);
  if (!pending) return undefined;
  clearTimeout(pending.timer);
  pendingLearningWrites.delete(wordKey);
  return pending.prevStatus;
}

/**
 * Cancel ALL pending learning writes without sending them. Call on logout/auth
 * reset so a deferred write can't fire with the next account's token, and any
 * time word state is being bulk-reset.
 */
export function cancelAllPendingLearningWrites(): void {
  for (const { timer } of pendingLearningWrites.values()) clearTimeout(timer);
  pendingLearningWrites.clear();
}

function persistStatus(
  word: UserVideoWord,
  lessonId: number,
  newStatus: UserWordStatus,
  prevStatus: UserWordStatus,
  // When true, also drop the priority-word tracker entry if the write fails —
  // used when this write superseded a cancelled (never-sent) learning write,
  // so a rollback must not leave a tracker entry for a status that never
  // persisted server-side.
  clearTrackerOnRollback = false,
): void {
  callWithAuth(putWordStatus, {
    wordKey: word.definition.wordKey,
    newStatus,
    definitionId: word.definition.definitionId,
    lessonId,
  })
    .then(() => {
      if (newStatus === "known") {
        // Clear any pending priority-word entry only AFTER the known write
        // persists — the word is no longer "newly learning", so the 55%/90%
        // regen must not treat it as such; on failure the tracker stays intact.
        priorityWordTracker.removeWordChange(word.definition.wordKey, lessonId);
        wordKnownToast$.set((n) => n + 1);
      }
      if (newStatus === "learning") {
        // iOS markCardsNeedRefresh(): a new "learning" word creates a review
        // card server-side, so refresh the review-count query (badge +
        // ReviewInsights/DictionaryHero).
        globalQueryClient.invalidateQueries({
          queryKey: [REVIEW_CARD_COUNT_QUERY_KEY],
        });
      }
    })
    .catch(() => {
      // Only roll back if our optimistic value is still in place (a later
      // toggle may have superseded it). Roll back to the TRUE persisted status.
      if (peekWordOverride(word.wordKey) === newStatus) {
        setWordOverride(word.wordKey, prevStatus);
      }
      if (newStatus === "learning" || clearTrackerOnRollback) {
        priorityWordTracker.removeWordChange(word.definition.wordKey, lessonId);
      }
    });
}

/**
 * First-tap "mark learning": optimistically flips a new/unknown word to
 * "learning" (instant UI + priority-word tracker), then persists after a short
 * defer so a follow-up double-tap (→ "known") cancels the learning write and
 * only "known" is saved — avoiding two status writes racing on the server.
 * No-op if the word is already known/learning.
 */
export function markWordLearning(word: UserVideoWord, lessonId: number): void {
  const currentStatus = peekWordOverride(word.wordKey) ?? word.status;
  if (currentStatus === "known" || currentStatus === "learning") return;

  const newStatus: UserWordStatus = "learning";
  setWordOverride(word.wordKey, newStatus);
  // Record the P1 (newly-learning) change so the 90% regen trigger can detect
  // words marked between 55% and 90% (mirrors iOS priorityWordTracker).
  priorityWordTracker.recordWordChange(
    word.definition.wordKey,
    lessonId,
    newStatus,
  );

  cancelPendingLearningWrite(word.wordKey);
  const timer = setTimeout(() => {
    pendingLearningWrites.delete(word.wordKey);
    persistStatus(word, lessonId, newStatus, currentStatus);
  }, LEARNING_WRITE_DEFER_MS);
  pendingLearningWrites.set(word.wordKey, {
    timer,
    prevStatus: currentStatus,
  });
}

/**
 * Toggle the word between "known" and "learning" with an optimistic local
 * override, persisting via `putWordStatus` and rolling back on failure.
 * Fires the "added to known" toast only after a successful save (matching the
 * phrase-save toast), and refreshes the review-count query on a "learning"
 * transition (iOS markCardsNeedRefresh — a learning word creates a review card).
 */
export function toggleWordKnown(word: UserVideoWord, lessonId: number): void {
  // Cancel any still-pending first-tap learning write for this word so it can't
  // later overwrite the status we're about to set (race fix across surfaces).
  const cancelledPrev = cancelPendingLearningWrite(word.wordKey);

  const currentStatus = peekWordOverride(word.wordKey) ?? word.status;
  const newStatus: UserWordStatus =
    currentStatus === "known" ? "learning" : "known";

  // If we just cancelled a never-sent learning write, the optimistic
  // "learning" never reached the server — roll back to that write's pre-tap
  // status, and drop its tracker entry on failure (it never persisted).
  const rollbackTo = cancelledPrev ?? currentStatus;

  setWordOverride(word.wordKey, newStatus);
  persistStatus(
    word,
    lessonId,
    newStatus,
    rollbackTo,
    cancelledPrev !== undefined,
  );
}
